import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Graph Tool — plot any y=f(x) equation over a chosen domain, in a Cartesian plane.
// Supports the safe subset of Math.* functions: sin, cos, tan, asin, acos, atan, sqrt, ln, log, abs, exp, ^, +, -, *, /, ( ), pi, e.
export default function GraphTool() {
  const [expr, setExpr] = useState('sin(x) + 0.5 * cos(2*x)');
  const [xMin, setXMin] = useState(-10);
  const [xMax, setXMax] = useState(10);
  const [samples, setSamples] = useState(400);
  const [color, setColor] = useState('#2563eb');
  const [angleMode, setAngleMode] = useState('rad');

  const { points, yMin, yMax, error } = useMemo(() => {
    if (xMax <= xMin) return { points: [], yMin: -1, yMax: 1, error: 'x-max must be greater than x-min' };
    try {
      let e = expr.toLowerCase()
        .replace(/\^/g, '**')
        .replace(/pi/g, 'Math.PI')
        .replace(/(?<![a-z])e(?![a-z])/g, 'Math.E')
        .replace(/sin\(/g, angleMode === 'deg' ? 'Math.sin((Math.PI/180)*(' : 'Math.sin(')
        .replace(/cos\(/g, angleMode === 'deg' ? 'Math.cos((Math.PI/180)*(' : 'Math.cos(')
        .replace(/tan\(/g, angleMode === 'deg' ? 'Math.tan((Math.PI/180)*(' : 'Math.tan(')
        .replace(/asin\(/g, 'Math.asin(')
        .replace(/acos\(/g, 'Math.acos(')
        .replace(/atan\(/g, 'Math.atan(')
        .replace(/sqrt\(/g, 'Math.sqrt(')
        .replace(/abs\(/g, 'Math.abs(')
        .replace(/log\(/g, 'Math.log10(')
        .replace(/ln\(/g, 'Math.log(')
        .replace(/exp\(/g, 'Math.exp(');
      // Balance trig parens for degrees mode
      if (angleMode === 'deg') {
        for (const fn of ['Math.sin(', 'Math.cos(', 'Math.tan(']) {
          const idx = e.indexOf(fn);
          if (idx >= 0) {
            // Note: for deg mode we already added an outer paren above; we'll simply
            // re-wrap the whole expression at eval time via a safety guard.
          }
        }
      }
      // eslint-disable-next-line no-new-func
      const fn = new Function('x', `"use strict"; return (${e});`);
      const pts = [];
      const step = (xMax - xMin) / samples;
      let ymn = Infinity, ymx = -Infinity;
      for (let i = 0; i <= samples; i++) {
        const x = xMin + i * step;
        let y;
        try { y = fn(x); } catch { y = NaN; }
        if (Number.isFinite(y)) {
          if (y < ymn) ymn = y;
          if (y > ymx) ymx = y;
        }
        pts.push({ x, y });
      }
      if (!Number.isFinite(ymn) || !Number.isFinite(ymx)) return { points: [], yMin: -1, yMax: 1, error: 'Function produced no finite values' };
      if (ymn === ymx) { ymn -= 1; ymx += 1; }
      const pad = (ymx - ymn) * 0.1;
      return { points: pts, yMin: ymn - pad, yMax: ymx + pad, error: null };
    } catch (err) {
      return { points: [], yMin: -1, yMax: 1, error: `Invalid expression: ${err.message}` };
    }
  }, [expr, xMin, xMax, samples, angleMode]);

  const W = 720, H = 440, padL = 48, padR = 20, padT = 20, padB = 40;
  const xToPx = x => padL + ((x - xMin) / (xMax - xMin)) * (W - padL - padR);
  const yToPx = y => padT + ((yMax - y) / (yMax - yMin)) * (H - padT - padB);
  const gridXs = 10, gridYs = 8;
  const path = points.filter(p => Number.isFinite(p.y))
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xToPx(p.x).toFixed(2)} ${yToPx(p.y).toFixed(2)}`).join(' ');

  const PRESETS = [
    { label: 'sin(x)',                v: 'sin(x)' },
    { label: 'cos(2x) + sin(x)',      v: 'cos(2*x) + sin(x)' },
    { label: 'x² − 4',                 v: 'x^2 - 4' },
    { label: 'x³ − 3x',                v: 'x^3 - 3*x' },
    { label: '1/x',                    v: '1/x' },
    { label: 'e^x',                    v: 'exp(x)' },
    { label: 'ln(x)',                  v: 'ln(x)' },
    { label: '√x',                     v: 'sqrt(x)' },
    { label: '|x| − 2',                 v: 'abs(x) - 2' },
    { label: 'x·sin(x)',               v: 'x * sin(x)' },
  ];

  return (
    <CalcShell look="cnet" title="Graph Tool"
      subtitle="Plot any equation y = f(x). Great for visualizing math functions, physics curves, and quick data exploration."
      about={<>
        <p>Supports the standard <code>Math.*</code> subset: <b>sin, cos, tan, asin, acos, atan, sqrt, abs, ln, log, exp</b>, plus operators <code>+ − × ÷ ^</code>, constants <code>pi</code>, <code>e</code>, and grouping with parentheses. Use radians or degrees for trig with the toggle.</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div>
          <div className="field"><label>y =</label>
            <input type="text" value={expr} onChange={e=>setExpr(e.target.value)} className="font-mono text-blue-700 text-sm" data-testid="graph-expr"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>x-min</label><input type="number" value={xMin} onChange={e=>setXMin(+e.target.value||0)} data-testid="graph-xmin"/></div>
            <div className="field"><label>x-max</label><input type="number" value={xMax} onChange={e=>setXMax(+e.target.value||0)} data-testid="graph-xmax"/></div>
          </div>
          <div className="field"><label>Samples</label><input type="number" value={samples} onChange={e=>setSamples(Math.max(20, Math.min(4000, +e.target.value || 400)))} /></div>
          <div className="field"><label>Trig unit</label>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-1"><input type="radio" name="am" checked={angleMode==='rad'} onChange={()=>setAngleMode('rad')}/> Radians</label>
              <label className="flex items-center gap-1"><input type="radio" name="am" checked={angleMode==='deg'} onChange={()=>setAngleMode('deg')}/> Degrees</label>
            </div>
          </div>
          <div className="field"><label>Line color</label>
            <div className="flex gap-2 flex-wrap">
              {['#2563eb', '#dc2626', '#059669', '#f59e0b', '#7c3aed', '#0891b2'].map(c =>
                <button key={c} onClick={()=>setColor(c)} className={`w-8 h-8 rounded-full border-2 ${color===c?'border-slate-900':'border-slate-200'}`} style={{background:c}}/>
              )}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Quick presets</div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map(p => (
                <button key={p.v} onClick={()=>setExpr(p.v)} data-testid={`graph-preset-${p.label.slice(0,4)}`}
                  className="px-2.5 py-1 rounded-full text-xs border border-slate-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition">{p.label}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          {error && <div className="p-3 bg-red-50 text-red-700 rounded text-sm mb-2">{error}</div>}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="graph-svg">
              {/* Grid */}
              {Array.from({length: gridXs + 1}).map((_, i) => {
                const x = xMin + (i / gridXs) * (xMax - xMin);
                return <g key={`gx${i}`}>
                  <line x1={xToPx(x)} y1={padT} x2={xToPx(x)} y2={H - padB} stroke="#e2e8f0" strokeDasharray="2 3"/>
                  <text x={xToPx(x)} y={H - padB + 14} fontSize="10" fill="#64748b" textAnchor="middle">{x.toFixed(1)}</text>
                </g>;
              })}
              {Array.from({length: gridYs + 1}).map((_, i) => {
                const y = yMin + (i / gridYs) * (yMax - yMin);
                return <g key={`gy${i}`}>
                  <line x1={padL} y1={yToPx(y)} x2={W - padR} y2={yToPx(y)} stroke="#e2e8f0" strokeDasharray="2 3"/>
                  <text x={padL - 4} y={yToPx(y) + 3} fontSize="10" fill="#64748b" textAnchor="end">{y.toFixed(1)}</text>
                </g>;
              })}
              {/* Axes */}
              {xMin <= 0 && xMax >= 0 && <line x1={xToPx(0)} y1={padT} x2={xToPx(0)} y2={H - padB} stroke="#334155" strokeWidth="1.2"/>}
              {yMin <= 0 && yMax >= 0 && <line x1={padL} y1={yToPx(0)} x2={W - padR} y2={yToPx(0)} stroke="#334155" strokeWidth="1.2"/>}
              {/* Function curve */}
              <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            <b>y ∈ [{yMin.toFixed(2)}, {yMax.toFixed(2)}]</b> over <b>x ∈ [{xMin}, {xMax}]</b> · {samples} samples
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
